/** Только подтверждённые и полностью применённые локальные правки не требуют своего WS-эха. */
interface SheetOperation {
  code: string;
  result: Promise<boolean>;
  resolve: (applied: boolean) => void;
  isApplied: (() => boolean) | null;
  timer?: ReturnType<typeof setTimeout>;
}
const operations = new Map<string, SheetOperation>();
const OPERATION_TTL_MS = 2 * 60_000;

function stillApplied(operation: SheetOperation): boolean {
  try { return operation.isApplied?.() ?? false; } catch { return false; }
}

function release(id: string, operation: SheetOperation): void {
  if (operations.get(id) === operation) operations.delete(id);
  if (operation.timer !== undefined) clearTimeout(operation.timer);
  operation.timer = undefined;
  // Callback замыкает весь прямоугольник. Освобождаем его даже если HTTP
  // завис и вызывающая сторона всё ещё хранит функцию подтверждения.
  operation.isApplied = null;
  operation.resolve(false);
}

export function trackSheetOperation(id: string, code: string, isApplied: () => boolean): (applied: boolean) => void {
  let settle!: (applied: boolean) => void;
  const result = new Promise<boolean>((resolve) => { settle = resolve; });
  const previous = operations.get(id);
  if (previous) release(id, previous);
  const operation: SheetOperation = { code, result, resolve: settle, isApplied };
  operations.set(id, operation);
  operation.timer = setTimeout(() => release(id, operation), OPERATION_TTL_MS);
  if (operations.size > 2000) {
    const [oldestId, oldest] = operations.entries().next().value!;
    release(oldestId, oldest);
  }
  return (applied) => operation.resolve(applied && stillApplied(operation));
}

export function ownSheetOperation(id: string | undefined, code: string): Promise<boolean> | undefined {
  const operation = id ? operations.get(id) : undefined;
  // Старое чтение или следующая правка могли заменить оптимистичные значения
  // как до HTTP-ответа, так и после него, пока WS-эхо было в пути.
  if (!operation || operation.code !== code) return undefined;
  return operation.result.then((applied) => {
    const matches = applied && stillApplied(operation);
    release(id!, operation);
    return matches;
  });
}

export function clearSheetOperations(): void {
  for (const [id, operation] of operations) release(id, operation);
}
